/**
 * EMRInputSection Component
 * Handles EMR-based patient input and selection
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Clipboard, List } from 'lucide-react';
import { PatientTableSection } from './PatientTableSection';

interface Patient {
  patient_clinic_data_id: number;
  full_name: string;
  dob: string;
  emr_id: string;
  provider_name: string;
  primary_dx: string;
  secondary_dx: string;
}

interface EMRInputSectionProps {
  // State
  patientEmrs: string;
  showEmrPatientList: boolean;
  emrPatients: Patient[];
  selectedEmrPatients: number[];
  emrSelectAll: boolean;
  currentPage: number;
  patientsPerPage: number;
  selectedClinic: string;
  isLoading: boolean;
  
  // Actions
  onPatientEmrsChange: (value: string) => void;
  onPasteEmrs: () => void;
  onShowEmrPatientList: () => void;
  onEmrPatientSelection: (patientId: number, checked: boolean) => void;
  onEmrSelectAll: (checked: boolean) => void;
  onCurrentPageChange: (page: number) => void;
  onPatientsPerPageChange: (perPage: number) => void;
}

export const EMRInputSection: React.FC<EMRInputSectionProps> = ({
  patientEmrs,
  showEmrPatientList,
  emrPatients,
  selectedEmrPatients,
  emrSelectAll,
  currentPage,
  patientsPerPage,
  selectedClinic,
  isLoading,
  onPatientEmrsChange,
  onPasteEmrs,
  onShowEmrPatientList,
  onEmrPatientSelection,
  onEmrSelectAll,
  onCurrentPageChange,
  onPatientsPerPageChange
}) => {
  const totalPages = Math.ceil(emrPatients.length / patientsPerPage);

  return (
    <div className="space-y-6">
      {/* Patient EMRs */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Patient EMRs*
        </label>
        <div className="space-y-2">
          <div className="text-sm text-gray-500 mb-1">Enter Patient's EMRs</div>
          <div className="flex space-x-2">
            <Textarea
              placeholder="Enter EMR IDs separated by commas (e.g., 15150, 15392, 15573)"
              value={patientEmrs}
              onChange={(e) => onPatientEmrsChange(e.target.value)}
              className="flex-1 min-h-[100px]"
            />
            <div className="flex flex-col space-y-2">
              <Button
                onClick={onPasteEmrs}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 whitespace-nowrap"
              >
                <Clipboard className="h-4 w-4 mr-2" />
                Paste EMRs
              </Button>
              <Button
                onClick={onShowEmrPatientList}
                disabled={!patientEmrs.trim() || !selectedClinic || isLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 whitespace-nowrap"
              >
                <List className="h-4 w-4 mr-2" />
                Show Patient List
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* EMR Patient List Table */}
      {showEmrPatientList && emrPatients.length > 0 && (
        <div className="mt-6">
          <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-96">
            <table className="w-full">
              <thead className="bg-purple-100 sticky top-0">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">
                    <input
                      type="checkbox"
                      id="select-all-emr-patients"
                      name="selectAllEmrPatients"
                      data-testid="select-all-emr-patients-checkbox"
                      className="mr-2 h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 accent-blue-600"
                      checked={emrSelectAll}
                      onChange={(e) => onEmrSelectAll(e.target.checked)}
                    />
                    Select All
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Full Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">DOB</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">EMR ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Provider Name</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Primary Dx</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Secondary Dx</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const startIndex = (currentPage - 1) * patientsPerPage;
                  const endIndex = startIndex + patientsPerPage;
                  const paginatedEmrPatients = emrPatients.slice(startIndex, endIndex);
                  return paginatedEmrPatients.map((patient) => (
                    <tr key={patient.patient_clinic_data_id} className="border-t border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          id={`emr-patient-checkbox-${patient.patient_clinic_data_id}`}
                          name={`emrPatientSelection-${patient.patient_clinic_data_id}`}
                          data-testid={`emr-patient-checkbox-${patient.patient_clinic_data_id}`}
                          className="h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 accent-blue-600"
                          checked={selectedEmrPatients.includes(patient.patient_clinic_data_id)}
                          onChange={(e) => onEmrPatientSelection(patient.patient_clinic_data_id, e.target.checked)}
                        />
                      </td>
                      <td className="py-3 px-4">{patient.full_name}</td>
                      <td className="py-3 px-4">{patient.dob}</td>
                      <td className="py-3 px-4">{patient.emr_id}</td>
                      <td className="py-3 px-4">{patient.provider_name}</td>
                      <td className="py-3 px-4">{patient.primary_dx}</td>
                      <td className="py-3 px-4">{patient.secondary_dx || 'null'}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          {/* EMR Pagination Controls */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select 
                value={patientsPerPage} 
                onChange={(e) => onPatientsPerPageChange(parseInt(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                {(() => {
                  const startIndex = (currentPage - 1) * patientsPerPage;
                  const endIndex = Math.min(startIndex + patientsPerPage, emrPatients.length);
                  return `${startIndex + 1}-${endIndex} of ${emrPatients.length}`;
                })()}
              </span>
              <div className="flex items-center space-x-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCurrentPageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="h-8 px-3"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCurrentPageChange(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="h-8 px-3"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected EMR Patients Count */}
      {selectedEmrPatients.length > 0 && (
        <div className="text-center py-4">
          <span className="text-lg font-medium text-purple-600">
            Selected Patients: {selectedEmrPatients.length}
          </span>
        </div>
      )}
    </div>
  );
};